import React from 'react';
import {
  Icon,
  Typeahead,
  Spacer,
  BlockList,
  Tag,
  TextCaption,
  ResourceCard,
  sendToast,
} from '@pypestream/design-system';
import { StudioResourceTypeEnum } from '@pypestream/api-services';

import { ConfigPanel } from '../src/config-panel';
import { tableStore } from '../../../stores/table-store';
import { useUploadPythonFiles } from '../../../hooks';
import { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';

import './resource-lib.scss';

export const ResourceLibPanel: React.FC<{
  resourceLibData?: ResourceLibItem[];
  onClose: () => void;
}> = ({ resourceLibData, onClose }) => {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedType, setSelectedType] =
    React.useState<StudioResourceTypeEnum | null>(null);
  const [filteredOptions, setFilteredOptions] = React.useState<
    ResourceLibItem[]
  >(resourceLibData || []);
  const [value, setValue] = React.useState<string>('');

  const handleTypeheadInput = (event: CustomEvent) => {
    setFilteredOptions(event.detail.filtered);
    setValue(event.detail.value);
  };

  const [isResourceLibAdding, setIsResourceLibAdding] = React.useState(false);
  const { handleUpload } = useUploadPythonFiles('scriptFile');

  const uploadScripts = async (scriptNames: string[]) => {
    try {
      const filePromises = scriptNames.map(async (name) => {
        const response = await fetch(`/scripts/${name}`);

        const contentType = response.headers.get('content-type');

        if (
          !response.ok ||
          (contentType && contentType.includes('text/html'))
        ) {
          console.error(`Script not found: ${name}`);
          return null;
        }

        const blob = await response.blob();
        return new File([blob], name, { type: 'text/x-python' });
      });

      const files = await Promise.all(filePromises);

      const dataTransfer = new DataTransfer();
      files.forEach((file) => file && dataTransfer.items.add(file));

      const fakeEvent = {
        target: { files: dataTransfer.files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await handleUpload(fakeEvent);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddResourceLib = async (item: ResourceLibItem) => {
    setIsResourceLibAdding(true);

    if (!item.data) {
      return;
    }

    try {
      const scriptNamesList = item.data
        .map((i) => i.command?.trim())
        .filter(Boolean)
        .map((cmd) => `${cmd}.py`);

      await uploadScripts(scriptNamesList);

      tableStore.addResourceLibRows(item.data);
    } catch (e) {
      console.error(e);

      sendToast({
        type: 'error',
        position: 'top-right',
        text: 'Failed to add Resource Library',
        autoClose: 3000,
      });
    } finally {
      setIsResourceLibAdding(false);
    }
  };

  return (
    <ConfigPanel title="Resource Library" onClose={onClose}>
      <div className="c-resource-lib__sticky-bar">
        <Typeahead
          searchKeys={['name']}
          data={resourceLibData as ResourceLibItem[]}
          onInput={handleTypeheadInput}
          value={value}
        />
        <Spacer />
        <Tag
          interactive
          minimal
          size="small"
          className="u-margin-right-xsmall"
          onclick={() =>
            setSelectedType((selected) =>
              selected === StudioResourceTypeEnum.MicroAgent
                ? null
                : StudioResourceTypeEnum.MicroAgent
            )
          }
          variant={
            selectedType === StudioResourceTypeEnum.MicroAgent
              ? 'indicator'
              : 'default'
          }
        >
          <Icon name="microagent" />
          Microagents
        </Tag>
        <Tag
          interactive
          minimal
          size="small"
          variant={
            selectedType === StudioResourceTypeEnum.ActionNode
              ? 'indicator'
              : 'default'
          }
          onclick={() =>
            setSelectedType((selected) =>
              selected === StudioResourceTypeEnum.ActionNode
                ? null
                : StudioResourceTypeEnum.ActionNode
            )
          }
        >
          <Icon name="action-node" />
          Action Nodes
        </Tag>
      </div>
      <Spacer size="xsmall" />
      <BlockList>
        {filteredOptions
          .filter((item) => {
            if (selectedType === null) {
              return true;
            }

            return item.type === selectedType;
          })
          .map((item) => (
            <ResourceCard
              key={item.id}
              interactive
              selected={item.id === selectedId}
              disabled={isResourceLibAdding}
              onClick={() => {
                setSelectedId((state) =>
                  state === item.id ? null : item.id || null
                );
                handleAddResourceLib(item);
                onClose();
              }}
              variant={
                item.type === StudioResourceTypeEnum.MicroAgent
                  ? 'default'
                  : 'neutral'
              }
              label={item.name}
              description={item.description}
            >
              <Icon
                name={
                  item.type === StudioResourceTypeEnum.MicroAgent
                    ? 'microagent'
                    : 'action-node'
                }
                size="small"
                slot="label-prefix"
              />
              {item.nodes && (
                <>
                  <Tag minimal size="small" slot="label-suffix">
                    {item.nodes}
                  </Tag>
                  <TextCaption variant="secondary" slot="label-suffix">
                    Nodes
                  </TextCaption>
                </>
              )}
              <Icon
                name="plus"
                size="small"
                slot="hover-action"
                color="purple"
                variant="filled"
                weight="medium"
              />
            </ResourceCard>
          ))}
      </BlockList>
    </ConfigPanel>
  );
};
